/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { MuiThemeProvider, Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import rehypeRaw from 'rehype-raw';
import ReactMarkdown from 'react-markdown';
import { BASE_URL, IMAGE_PATHS } from '../../../constants/paths.constants';
import { axuminTheme, poslumaTheme, purple, poslumaDarkBlue, transparentGrey } from '../../../app/theme';
import PresentationTab from './PresentationTab';
import ImagingTab from './ImagingTab';
import FindingsTab from './FindingsTab';
import CASE_STUDY, { CaseStudyFormat } from '../../../constants/caseStudy.constants';

const useStyles = makeStyles(() => ({
  container: {
    paddingBottom: 50,
  },
  header: {
    display: 'flex',
    marginBottom: 50,
  },
  headshotImage: {
    height: '100%',
    maxHeight: 146,
    paddingLeft: 32,
    paddingRight: 43,
    marginBottom: 10,
  },
  rightSideHeader: {
    paddingRight: 125,
  },
  axuminSubtitle: {
    color: purple,
    fontSize: 18,
    fontStyle: 'italic',
    paddingBottom: 14,
  },
  poslumaSubtitle: {
    color: poslumaDarkBlue,
    fontSize: 18,
    fontStyle: 'italic',
    paddingBottom: 14,
  },
  caseStudyHeader: {
    marginBottom: 0,
  },
  legalText: {
    margin: '0px 0 10px 0',
    fontSize: 14,
  },
  tabContainer: {
    marginLeft: 90,
    marginRight: 90,
  },
  tabImageRow: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '-1px',
    width: '100%',
  },
  tabs: {
    width: '95%',
  },
  axuminBorder: {
    border: `1px solid ${purple}`,
    borderTopRightRadius: 24,
    minHeight: 564,
    paddingBottom: 8,
  },
  poslumaBorder: {
    border: `1px solid ${poslumaDarkBlue}`,
    borderTopRightRadius: 24,
    minHeight: 564,
    paddingBottom: 8,
  },
  tabContents: {
    paddingTop: 28,
    paddingLeft: 54,
    paddingRight: 24,
  },
  tabHeader: {
    display: 'flex',
    flexDirection: 'row',
  },
  headerLeftSide: {
    width: '100%',
  },
  headerRightSide: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  navButtonsIcon: {
    height: 56,
    width: 56,
  },
  notesBox: {
    width: '70%',
    marginLeft: 'calc(15% - 30px)',
    padding: 10,
    boxSizing: 'border-box',
    border: `1px solid ${purple}`,
    backgroundColor: transparentGrey,
    marginBottom: 10,
    '& > p': {
      fontSize: 16,
      color: purple,
      margin: 6,
      textAlign: 'center',
    },
  },
}));

type CaseStudyProps = {
  trainingId: number;
  module: string;
  page: number;
  index?: number;
  setNextDisabledCS?: (disabled: boolean) => void;
};

const CaseStudy = ({ trainingId, module, page, index, setNextDisabledCS }: CaseStudyProps): JSX.Element => {
  const classes = useStyles();
  const theme = trainingId === 1 ? axuminTheme : poslumaTheme;
  const caseStudyData =
    index !== undefined
      ? (CASE_STUDY[module][page.toString()] as CaseStudyFormat[])[index] || {}
      : (CASE_STUDY[module][page.toString()] as CaseStudyFormat) || {};
  const { headshot, noteParagraphs, title } = caseStudyData;
  const caseStudyPath = IMAGE_PATHS.CASE_STUDY[trainingId.toString() as keyof typeof IMAGE_PATHS.CASE_STUDY];

  const [tab, setTab] = useState(1);
  const [nextIcon, setNextIcon] = useState(caseStudyPath.CASE_STUDY_NEXT_ENABLED);
  const [prevIcon, setPrevIcon] = useState(caseStudyPath.CASE_STUDY_PREVIOUS_DISABLED);
  const [tabImage, setTabImage] = useState({
    src: caseStudyPath.CASE_STUDY_TAB_1.path,
    alt: caseStudyPath.CASE_STUDY_TAB_1.alt,
  });

  useEffect(() => {
    setNextIcon(tab === 3 ? caseStudyPath.CASE_STUDY_NEXT_DISABLED : caseStudyPath.CASE_STUDY_NEXT_ENABLED);
    setPrevIcon(tab === 1 ? caseStudyPath.CASE_STUDY_PREVIOUS_DISABLED : caseStudyPath.CASE_STUDY_PREVIOUS_ENABLED);

    switch (tab) {
      case 1: {
        setTabImage({
          src: caseStudyPath.CASE_STUDY_TAB_1.path,
          alt: caseStudyPath.CASE_STUDY_TAB_1.alt,
        });
        break;
      }
      case 2: {
        setTabImage({
          src: caseStudyPath.CASE_STUDY_TAB_2.path,
          alt: caseStudyPath.CASE_STUDY_TAB_2.alt,
        });
        break;
      }
      case 3: {
        setTabImage({
          src: caseStudyPath.CASE_STUDY_TAB_3.path,
          alt: caseStudyPath.CASE_STUDY_TAB_3.alt,
        });
        break;
      }
      default:
    }
  }, [tab]);

  const handleClickPrevious = () => {
    setTab(tab - 1);
  };

  const handleClickNext = () => {
    if (tab === 2 && setNextDisabledCS) {
      setNextDisabledCS(false);
    }
    setTab(tab + 1);
  };

  const renderTab = () => {
    switch (tab) {
      case 1: {
        return <PresentationTab module={module} page={page} index={index} trainingId={trainingId} />;
      }
      case 2: {
        return <ImagingTab module={module} page={page} index={index} />;
      }
      case 3: {
        return <FindingsTab module={module} page={page} index={index} trainingId={trainingId} />;
      }
      default: {
        return null;
      }
    }
  };

  return (
    <MuiThemeProvider theme={theme}>
      <div className={classes.container}>
        <div className={classes.header}>
          <img className={classes.headshotImage} src={`${BASE_URL}${headshot.path}`} alt={`${headshot.alt}`} />
          <div className={classes.rightSideHeader}>
            <Typography variant="h4" className={classes.caseStudyHeader}>
              {title || 'Case Study'}
            </Typography>
            <Typography variant="body1" className={classes.legalText}>
              Photo is not of an actual patient.
            </Typography>
            <Typography className={trainingId === 1 ? classes.axuminSubtitle : classes.poslumaSubtitle}>
              To advance, please first review the Presentation, Imaging, and Findings in the following case study by
              selecting the
              {trainingId === 1 ? ' purple ' : ' blue '} arrow
            </Typography>
            <Typography variant="body1">
              <b>Disclaimer: </b>
              These case studies are provided as examples of the interpretation and utility of
              {trainingId === 1 ? ' Axumin' : ' POSLUMA '}
              {trainingId === 1 ? (
                <>
                  <sup>®</sup>
                </>
              ) : (
                ''
              )}
              {trainingId === 1
                ? ' (fluciclovine F 18) PET/CT imaging results. Axumin '
                : ' PET imaging results. POSLUMA '}
              is not indicated for directing or changing patient management. Such decisions must be based on the
              independent medical judgment of the physician(s) responsible for the individual patient’s management
              {module === 'additional' ? '. Does not include all clinical information needed to assess the case.' : '.'}
              {trainingId === 2
                ? ' These studies do not include all clinical information needed to assess the case.'
                : ''}
            </Typography>
          </div>
        </div>
        <div className={classes.tabContainer}>
          <div className={classes.tabImageRow}>
            <img className={classes.tabs} src={`${BASE_URL}${tabImage.src}`} alt={`${tabImage.alt}`} />
          </div>
          <div className={trainingId === 1 ? classes.axuminBorder : classes.poslumaBorder}>
            <div className={classes.tabContents}>
              <div className={classes.tabHeader}>
                <div className={classes.headerLeftSide}>{renderTab()}</div>
                <div className={classes.headerRightSide}>
                  <IconButton disabled={tab === 1} onClick={handleClickPrevious}>
                    <img
                      className={classes.navButtonsIcon}
                      src={`${BASE_URL}${prevIcon.path}`}
                      alt={`${prevIcon.alt}`}
                    />
                  </IconButton>
                  <IconButton disabled={tab === 3} onClick={handleClickNext}>
                    <img
                      className={classes.navButtonsIcon}
                      src={`${BASE_URL}${nextIcon.path}`}
                      alt={`${nextIcon.alt}`}
                    />
                  </IconButton>
                </div>
              </div>
              {noteParagraphs && tab === 3 && (
                <div className={classes.notesBox}>
                  {noteParagraphs.map((item) => (
                    <ReactMarkdown key={item} rehypePlugins={[rehypeRaw]}>
                      {item}
                    </ReactMarkdown>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </MuiThemeProvider>
  );
};

export default CaseStudy;
